import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";
import { Button, Column, DataTable, FilterBlock, Hyperlink } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { normalizeForSearch } from "@racoon/lib";
import { useListTeamsQuery } from "~/shared/api/team/team.grpc";
import { useTeamModal } from "./use-team-modal";

type Team = ConnectProto.Team;

interface TeamsSettingsProps {
  selectedCompanyId?: number;
}

export const TeamsSettings: React.FC<TeamsSettingsProps> = ({ selectedCompanyId }) => {
  const t = useT();
  const [searchValue, setSearchValue] = useState("");

  // `currentData`, not `data`: after an org switch `data` still holds the previous org's teams.
  const { currentData: data, isFetching } = useListTeamsQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );

  const teams = useMemo(() => {
    const all = data?.teams ?? [];
    const query = normalizeForSearch(searchValue.trim());
    return query ? all.filter((team) => normalizeForSearch(team.name).includes(query)) : all;
  }, [data, searchValue]);

  const openTeamModal = useTeamModal(selectedCompanyId);

  const columns = useMemo<Column<Team>[]>(
    () => [
      { id: "index", header: "№", accessor: (_r, i) => i + 1, width: 64 },
      {
        id: "name",
        header: t(Namespace.Settings, "teams.table.name"),
        accessor: (row) => row.name,
        cell: ({ row }) => (
          <Hyperlink onOpen={() => openTeamModal(row.original)}>{row.original.name}</Hyperlink>
        ),
        sortable: true,
        width: 300,
      },
      {
        id: "membersCount",
        header: t(Namespace.Settings, "teams.table.membersCount"),
        accessor: (row) => row.users.length,
        sortable: true,
        width: 200,
      },
    ],
    [t, openTeamModal],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <FilterBlock
        direction="row"
        bgColor="white"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        additionalButtonsPosition="bottom"
        additionalButtons={
          <Button
            size="sm"
            variant="opacityBlueWithoutOutline"
            className="gap-1"
            onClick={() => openTeamModal(null)}
          >
            <Plus className="size-4" />
            {t(Namespace.Settings, "teams.addButton")}
          </Button>
        }
      />

      <div className="min-h-0 flex-1 rounded-lg">
        <DataTable
          className="h-full"
          columns={columns}
          data={teams}
          loading={isFetching}
          rowKey={(row) => row.id.toString()}
          namespace={Namespace.Settings}
          empty={
            <p className="p-8 text-center text-gray-500">
              {t(Namespace.Settings, "teams.table.empty")}
            </p>
          }
        />
      </div>
    </div>
  );
};
